import { loginPathForLocation } from '@/utils/homeNavigation';
import { LinkOutlined } from '@ant-design/icons';
import { ConfigProvider, theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
/**
 * 从 pro-layout 子包直接引入，不要走 '@ant-design/pro-components'：
 * 后者被 alias 指向 src/components/ResizableTable/pro-components-proxy，
 * 该 proxy 用 `export *` 重导出 10 个子包（tree-shaking 失效），会把整套 pro-*
 * 与 @dnd-kit 一并拽进 umi 入口 chunk。SettingDrawer 由 pro-layout 导出，组件本身相同。
 */
import { SettingDrawer } from '@ant-design/pro-layout';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
export { patchLegacyBillClientRoutes as patchClientRoutes } from '@/utils/legacyBillRedirect';
import TabLayout from '@/components/TabLayout';
import RealtimeProvider from '@/realtime/RealtimeProvider';
import PdaRealtimeBridge from '@/realtime/PdaRealtimeBridge';
import SessionHeartbeat from '@/components/SessionHeartbeat';
import MegaMenuContent from '@/components/MegaMenuContent';
// 引入部署修复样式
import './styles/deployment-fixes.css';
// 引入全局响应式样式
import './styles/responsive.css';

import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getCurrentUserMenus } from '@/services/ant-design-pro/menu';
import defaultSettings, { getLayoutToken } from '../config/defaultSettings';
import { LIGHT_SEEDS } from '@/constants/antdThemeSeeds';
import { errorConfig } from './requestErrorConfig';
import {
  isAuthenticationExemptPath,
  isPdaPagePath,
  isStandalonePagePath,
} from '@/utils/appRouteAccess';
import { convertAuthoritativeBackendUserToCurrentUser } from '@/utils/userConverter';
import { clearTagsViewTabs } from '@/utils/tagsViewStorage';
import { convertBackendMenusToProLayout } from '@/utils/backendMenuNavigation';
import { buildSidebarGroups } from '@/utils/sidebarGrouping';
import { SIDEBAR_GROUPS } from '@/config/sidebarGroups';
import { initDensity } from '@/hooks/useDensity';
import { initScreenTier } from '@/constants/screenTier';
import { useScreenTier } from '@/hooks/useScreenTier';
import { beginRoutePageLoading, finishBootPageLoading } from '@/utils/pageLoading';
// 运行期主题：store 负责广播（判据与落盘仍在 utils/theme.ts）
import { useDarkMode } from '@/utils/darkMode';
// ⚠️ 临时导航性能探针，排查结束后连同 utils/navPerfProbe.ts 一并删除
import { installNavPerfProbe, navPerf } from '@/utils/navPerfProbe';

installNavPerfProbe();

// 🔑 全局密度初始化：在登录页等所有页面渲染之前应用密度到 html font-size
// 解决「登录页不渲染 TabLayout，靠 hook 设置根字号会先按 16px 渲染再跳」的首帧闪跳。
// ⚠️ 密度已不再是用户偏好（工具栏密度按钮已移除）：它由屏幕档位自动派生，
//    这里直接由视口宽度解析 → 档位 → 密度，因此与下一行的 initScreenTier() 谁先谁后都一样。
initDensity();

// 🔑 全局屏幕档位初始化：写入 <html data-tier="A|B|C|D">，驱动 styles/tier-tokens.css
// 与 initDensity() 同时机、同样必须在任何页面渲染前完成，否则首帧会用错档位。
// 分工：档位管结构（侧栏宽度/行高/列数/触控热区），密度管字号，两者正交。
initScreenTier();

import dayjs from 'dayjs'; // 引入 dayjs
import 'dayjs/locale/zh-cn'; // 引入中文语言包
dayjs.locale('zh-cn'); // 设置 dayjs 语言为中文
import zhCN from 'antd/lib/locale/zh_CN'; // 引入 Ant Design 的中文语言包

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * 读取档位尺寸（styles/tier-tokens.css 的 --tier-* 变量）。
 *
 * ProLayout 的 siderWidth / collapsedWidth 只吃 number，而侧栏宽度的唯一事实来源
 * 是 CSS 档位变量（<html data-tier> 驱动），所以这里在运行时把同一份变量读成数字，
 * 避免「CSS 一套、JS 又硬编码一套」的不一致。
 * 回退值 = 改造前的旧值，仅用于 CSS 尚未加载的极端时序。
 * 视觉上 CSS 始终是权威：窄档下的宽度还有 multi-level-menu.css 的 !important 兜底。
 */
function readTierPx(variableName: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// 抑制开发环境中的常见警告
if (typeof window !== 'undefined') {
  // 覆盖 console.error 来过滤常见警告
  const originalError = console.error;
  console.error = (...args: any[]) => {
    // 过滤掉 React Intl 的缺失翻译警告
    if (args[0] && typeof args[0] === 'string' && args[0].includes('[React Intl] Missing message')) {
      return; // 静默处理，不输出警告
    }
    // 过滤掉 findDOMNode 的废弃警告（来自 Ant Design/ProComponents 内部）
    if (args[0] && typeof args[0] === 'string' && args[0].includes('findDOMNode is deprecated')) {
      return; // 静默处理，不输出警告
    }
    // 过滤掉 hiprint WebSocket 连接错误（hiprint 尝试连接不存在的服务器）
    const errorMessage = args[0]?.toString() || '';
    if (errorMessage.includes('WebSocket') && 
        (errorMessage.includes('socket.io') || 
         errorMessage.includes('17521') ||
         errorMessage.includes('TransportError'))) {
      return; // 静默处理，不输出 WebSocket 连接错误
    }
    // 其他错误正常输出
    originalError.apply(console, args);
  };
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  userMenus?: any[]; // 用户可访问的菜单列表
  loading?: boolean;
  sidebarCollapsed?: boolean; // 侧边栏折叠状态
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  fetchUserMenus?: () => Promise<any[]>;
}> {
  /**
   * 🔑 `initialState.settings` **不随主题变化**（ProLayout 的 navTheme / token 也不例外）。
   *
   * 原因：主题是「切一次要立刻见效」的高频交互，而写 initialState 会让**整棵
   * @@initialState 树重渲染**——包括 umi 的 layout 运行时（它一重跑，ProLayout
   * 连同内容区一起重建），这正是「切主题卡一下、不如左侧菜单丝滑」的主因。
   * 运行期主题改由 utils/darkMode 的 store 广播（只有真正消费主题的组件订阅），
   * 这里只放与主题无关的静态设置。
   */
  const initialSettings = {
    ...defaultSettings,
  } as Partial<LayoutSettings>;

  const fetchUserInfo = async () => {
    try {
      // 检查是否有token，如果没有token则不请求用户信息，避免不必要的请求
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return undefined;
      }
      
      // 调用后端API获取用户信息
      // 后端返回格式: { success: true, data: User }
      // UmiJS request返回的是整个响应对象，所以需要访问 response.data
      const response = await queryCurrentUser();
      
      // response可能是 { data: User } 格式，或者直接是 { success: true, data: User }
      // 需要检查实际的数据结构
      const backendUser = 'success' in response ? response.data : response;

      // currentUser 是权限的权威来源；空 data 或缺少 permissions 时必须失败关闭。
      return convertAuthoritativeBackendUserToCurrentUser(backendUser);
    } catch (_error) {
      // 移除token，避免重定向循环
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      // 只在非登录页面时才跳转到登录页，避免循环
      const { pathname } = history.location;
      // PDA 小应用自成一套鉴权，未登录跳其专属登录页而非 PC 登录页
      if (isPdaPagePath(pathname)) {
        if (pathname !== '/pda/login') history.replace('/pda/login');
      } else if (
        !isAuthenticationExemptPath(pathname) &&
        pathname !== loginPath &&
        !pathname.startsWith('/user/')
      ) {
        history.replace(loginPathForLocation(history.location));
      }
    }
    return undefined;
  };

  /**
   * 获取用户菜单权限
   * 🔑 核心权限控制：从后端获取当前用户可访问的菜单列表
   */
  const fetchUserMenus = async () => {
    try {
      // const response = await getCurrentUserMenus(); // 已移除
      const response = await getCurrentUserMenus();
      
      if (response.success && response.data) {
        return response.data || [];
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  };
  
  // 只有对外展示页面允许未登录访问，其他页面（包括登录页和注册页）都需要登录
  // PDA 小应用（/pda/*）自成一套鉴权，boot 时不走 PC 端 currentUser 校验，避免被弹到 PC 登录页
  const { location } = history;
  const isAuthenticationExempt = isAuthenticationExemptPath(
    location.pathname,
  );

  // 如果是登录页或注册页，也需要检查登录状态
  const isLoginOrRegisterPage = 
    location.pathname === loginPath ||
    location.pathname === '/user/register' ||
    location.pathname === '/user/register-result';
  
  // 如果不是对外展示页面，执行（包括登录页和注册页也需要登录）
  if (!isAuthenticationExempt) {
    // 先快速检查token，如果有token再请求用户信息
    // 这样可以避免不必要的等待
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      // ⚡ 菜单与用户信息两个请求互不依赖（都只依赖上面读到的 token），并发发出以重叠
      // 一次网络往返。下面的返回对象在两个都 await 之后才构建，故不存在提交次序问题；
      // currentUser 失败提前返回时多发出的那次菜单请求由 fetchUserMenus 内部 catch 吞掉。
      const menusPromise = fetchUserMenus();

      const currentUser = await fetchUserInfo();
      if (!currentUser) {
        return {
          fetchUserInfo,
          fetchUserMenus,
          settings: initialSettings,
        };
      }

      // 🔑 获取用户菜单权限
      const userMenus = await menusPromise;
      
      return {
        fetchUserInfo,
        fetchUserMenus,
        currentUser,
        userMenus,
        settings: initialSettings,
      };
    }
    // 如果没有token，直接返回，不请求用户信息
    return {
      fetchUserInfo,
      fetchUserMenus,
      settings: initialSettings,
    };
  }
  return {
    fetchUserInfo,
    fetchUserMenus,
    settings: initialSettings,
  };
}

/**
 * antd ConfigProvider 的 theme —— **模块级常量，不随主题变化**（切主题的关键前提）。
 *
 * 为什么必须与主题解耦：theme 对象一旦随主题换引用，antd 的 DesignTokenContext 就换值，
 * 全站每个 antd 组件都要重渲染 —— 这才是切主题真正的代价（与 cssinjs 是否重新生成
 * 样式无关，cssVar 模式下色值都是 var(--ant-*) 间接引用）。解耦后切主题只翻
 * `<html data-theme>` 一个属性：吃 CSS 变量的面同帧翻转，React 侧零工作量。
 *
 * 暗色从哪来：src/styles/antdDarkTokens.css（生成件，见 scripts/gen-antd-dark-tokens.cjs）
 * 按**本仓两套种子**枚举出亮暗不同的全部 antd token，在 `[data-theme="dark"]` 下逐条覆盖。
 * 因此这里的 token 只写亮色一套，且必须与那个脚本同源（LIGHT_SEEDS）。
 * ⚠️ 改种子 / 升级 antd 后都要重跑该脚本，否则暗色会缺项或与亮色不同步。
 *
 * 定在模块级还顺手断了一条老路：以前它是渲染里的内联字面量，每次渲染都是新引用，
 * cssinjs 的按引用缓存全部未命中。
 */
const ANT_D_CONFIG_THEME: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  // key 必须与 config/config.ts 一致：它就是暗色覆盖层（antdDarkTokens.css）的命中类名
  cssVar: { key: 'linson-ds' },
  token: {
    // 禁用所有动画效果以兼容React 17
    motion: false,
    ...LIGHT_SEEDS,
  },
  components: {
    Button: {
      // 禁用按钮波纹效果
      motion: false,
    },
  },
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 监听logout事件，清除用户信息
  React.useEffect(() => {
    const handleLogout = () => {
      setInitialState((preInitialState) => ({
        ...preInitialState,
        currentUser: undefined,
        userMenus: undefined,
      }));
      // 任何触达「登出事件」的路径都同步清空 tagsview 页签持久化数据，
      // 保证换账号后不会残留上一个账号的页签。
      clearTagsViewTabs();
    };
    window.addEventListener('logout', handleLogout);
    return () => {
      window.removeEventListener('logout', handleLogout);
    };
  }, [setInitialState]);

  // 🔑 运行期主题取自 utils/darkMode 的 store：它只通知真正消费主题的组件，
  //    不写 initialState（写它会让整棵 @@initialState 树重渲染，包括下面这个
  //    layout 运行时自己 —— 那会把 ProLayout 连同内容区一起重建）。
  const darkMode = useDarkMode();

  // 侧边栏折叠状态（通过 initialState 共享，TabLayout 可读取/切换）
  const collapsed = initialState?.sidebarCollapsed || false;
  const setCollapsed = React.useCallback(
    (val: boolean) => {
      setInitialState((prev) => ({ ...prev, sidebarCollapsed: val }) as any);
    },
    [setInitialState],
  );

  // 🔑 侧栏尺寸由屏幕档位驱动（A=216/56，B=236/56，C=56/56，D=0/0）。
  // 值与 styles/tier-tokens.css 的 --tier-* 同源（运行时读同一份 CSS 变量），
  // tier 变化时重新解析，避免「CSS 一套、JS 硬编码另一套」。
  const { tier } = useScreenTier();
  const sidebarWidth = React.useMemo(
    () => readTierPx('--tier-sider-w', 216),
    [tier],
  );
  const sidebarCollapsedWidth = React.useMemo(
    () => readTierPx('--tier-sider-collapsed-w', 56),
    [tier],
  );

  /**
   * antd 的 ConfigProvider theme 见模块级常量 ANT_D_CONFIG_THEME：它**不随主题变化**，
   * 这里的布局重渲染必须拿不到新引用（否则 antd 的 DesignTokenContext 换值，
   * 全站每个 antd 组件跟着重渲染）。
   */

  /**
   * 🔑 ProLayout token 同样要 memo 化（同一个 cssinjs 缓存问题）。
   * getLayoutToken 每次调用都返回新对象；直接内联进 layout props 会让 ProLayout
   * 每次渲染都重新生成侧栏/顶栏样式。
   *
   * 它是**唯一**还随主题变化的下发项，这是故意的：navTheme 决定 ProLayout 给
   * antd Menu 的 `theme="dark"/"light"`，而 Menu 内部预设色不吃我们的 CSS 层。
   * 代价只是 ProLayout 子树重渲染一次（内容区因元素引用未变而不重建），
   * 与「全站每个 antd 组件重渲染」不是一个量级。
   */
  const layoutToken = React.useMemo(() => getLayoutToken(darkMode), [darkMode]);

  // 🔑 检查当前路径是否是独立页面（layout: false）
  const currentPath = history.location.pathname;
  const isStandalonePage = isStandalonePagePath(currentPath);


  // 侧边栏折叠状态已通过 initialState.sidebarCollapsed 管理，不再需要 DOM 注入按钮

  // 侧边栏显示/隐藏时触发 window resize，强制内容区域和图表重绘
  React.useEffect(() => {
    if (isStandalonePage) return;
    // 延迟触发，等 CSS 生效后再通知页面
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 150);
    return () => clearTimeout(timer);
  }, [collapsed, isStandalonePage]);

  return {
    /**
     * 🔑 核心权限控制：使用后端菜单数据 + 路由配置中的图标和名称
     * 后端菜单数据用于权限控制，路由配置用于显示图标和名称
     */
    /**
     * ⚠️ 这里的 useCallback 不是优化洁癖，是在修一个真实的性能缺陷，别去掉。
     *
     * ProLayout 内部对菜单结果做了 useMemo，而它的依赖里**包含 menuDataRender 本身**：
     *   app.tsx → node_modules/@ant-design/pro-layout/es/ProLayout.js
     *   useMemo(() => getMenuData(…, menuDataRender),
     *           [formatMessage, menu, menuDataRender, data, route?.children, route?.routes])
     * 本函数原先写成内联箭头 → 每次渲染都是新身份 → 该 memo 永远不命中 →
     * `transformRoute`（整棵路由树转换）与我们的菜单管线在**任何** layout 重渲染时
     * 都全量重跑一遍：切主题、切页签、折叠侧栏、任何 initialState 变化都算。
     * 重跑的代价不只是那十几毫秒 —— 它产出**全新的菜单对象**，于是侧栏 / 面包屑 /
     * 页签标题 / 菜单搜索的 memo 一起失效。这正是「切主题卡一下、部分模块迟钝」的来源之一。
     *
     * 依赖只取 initialState.userMenus 的引用：主题切换改的是 darkMode 与 settings，
     * 不动 userMenus，于是身份稳定、memo 命中；菜单权限真正变化时才会重算。
     * 额外带上 isStandalonePage：函数体里那个「独立页面返回空菜单」的分支读的是**实时**
     * 路径，而 ProLayout 的 memo 依赖里没有 path —— 不把它计入依赖，跨越独立页边界时
     * memo 会命中并返回上一次的结果。带上它 = 跨越边界时身份变化，语义与改动前一致。
     * 函数体内其余读取（history.location、navPerf、SIDEBAR_GROUPS、window 写入）
     * 要么是模块级常量，要么是当次现读，不构成依赖。
     */
    menuDataRender: React.useCallback((menuData: any) => {
      // 在函数内部重新计算当前路径，确保获取最新的路径
      const currentPathInRender = history.location.pathname;
      const isStandalonePageInRender = isStandalonePagePath(
        currentPathInRender,
      );
      
      // 如果是独立页面，不渲染菜单
      if (isStandalonePageInRender) {
        return [];
      }
      
      // 如果没有用户菜单权限数据，不显示任何菜单
      if (!initialState?.userMenus || initialState.userMenus.length === 0) {
        return [];
      }
      
      // 🔑 使用后端返回的菜单数据构建菜单树，并合并路由配置中的图标和名称
      const __perfT0 = navPerf.enabled ? performance.now() : 0;
      const proLayoutMenus = convertBackendMenusToProLayout(initialState.userMenus, menuData || []);
      if (navPerf.enabled) navPerf.menuPipeline(performance.now() - __perfT0);

      /**
       * 🔑 T5 菜单分块：把一级列表按 config/sidebarGroups.ts 的名单装配成组。
       * - 名单为空 / 只有一组 → 不分组，保持原顺序原样返回（零风险退化）
       * - 分组时把分组信息回写到菜单项上（__sidebarGroupKey / __sidebarGroupTitle），
       *   MegaMenuContent 直接消费，无需依赖 window 全局
       * - 未命中任何分组的菜单项进兜底组，绝不丢弃（见 utils/sidebarGrouping.ts）
       */
      const sidebarGroups = buildSidebarGroups(proLayoutMenus as any[], SIDEBAR_GROUPS);
      const grouped = sidebarGroups.length > 1;
      if (grouped) {
        for (const bucket of sidebarGroups) {
          for (const item of bucket.items) {
            (item as any).__sidebarGroupKey = bucket.group.key;
            (item as any).__sidebarGroupTitle = bucket.group.title;
          }
        }
      }
      // 组顺序 = 名单顺序；未分组时顺序不变
      const orderedMenus = grouped
        ? sidebarGroups.flatMap((bucket) => bucket.items)
        : proLayoutMenus;

      // 将菜单数据保存到全局变量，供TabLayout使用
      if (typeof window !== 'undefined') {
        // ⚠️ __MENU_DATA__ 契约不变：仍是「一级菜单的扁平树（children 递归）」，
        //    只在分组生效时按组顺序重排，TabLayout 的页签标题 / 面包屑 / 菜单搜索逻辑不受影响。
        (window as any).__MENU_DATA__ = orderedMenus;
        (window as any).__ROUTE_MENU_DATA__ = menuData || []; // 保存原始路由配置，供TabLayout回退查找
        // 新增 key：分组结果本身（供调试 / 后续消费方使用，不影响既有读取方）
        (window as any).__MENU_GROUPS__ = sidebarGroups.map((bucket) => ({
          key: bucket.group.key,
          title: bucket.group.title,
          itemKeys: bucket.items.map((item) => item.key),
        }));
      }

      // 调试：输出菜单数据结构
      return orderedMenus;
    }, [initialState?.userMenus, isStandalonePage]),
    // 使用 MegaMenuContent 实现平铺菜单效果
    menuContentRender: (props) => {
      return <MegaMenuContent {...props} />;
    },
    /**
     * 🔑 自定义菜单项渲染，支持在当前页面内打开多个标签页
     * 普通点击：在当前页面内打开新标签页
     * Ctrl/Cmd + 点击：在新浏览器标签页打开
     */
    menuItemRender: (menuItemProps: any, defaultDom: React.ReactNode) => {
      const { path, onClick } = menuItemProps;
      
      if (!path) {
        return defaultDom;
      }

      return (
        <a
          href={path}
          onClick={(e) => {
            // Ctrl/Cmd + 点击：在新浏览器标签页打开
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const url = `${window.location.protocol}//${window.location.host}${path}`;
              window.open(url, '_blank', 'noopener,noreferrer');
              return false;
            }
            
            // 普通点击：在当前页面内导航（TabLayout会处理标签页）
            e.preventDefault();
            
            // 确保路由跳转
            if (onClick) {
              // 如果菜单项有自定义onClick，先执行它
              try {
                const result = onClick(e);
                // 如果onClick返回false，不执行路由跳转
                if (result === false) {
                  return false;
                }
              } catch (error) {
                
              }
            }
            
            // 执行路由跳转（使用 replace 避免打开新页签）
            history.replace(path);
            return false;
          }}
          onMouseDown={(e) => {
            // 中键点击：在新浏览器标签页打开
            if (e.button === 1) {
              e.preventDefault();
              const url = `${window.location.protocol}//${window.location.host}${path}`;
              window.open(url, '_blank', 'noopener,noreferrer');
              return false;
            }
            return undefined;
          }}
          style={{ textDecoration: 'none' }}
        >
          {defaultDom}
        </a>
      );
    },
    /**
     * 菜单交互由 MegaMenuContent 的点击展开抽屉面板接管
     */
    menuProps: {
      mode: 'vertical',
      inlineIndent: 0,
      overflow: 'visible',
    },
    // 🔑 搜索、语言切换、通知、登录人已移至 TabLayout 标签栏右侧
    actionsRender: () => [],
    avatarProps: undefined,
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => null,
    // 独立页面隐藏侧边栏菜单和顶栏，全屏显示（设计器页面自带 header）
    layout: isStandalonePage ? 'top' : 'side',
    menuRender: isStandalonePage ? false : undefined,
    headerRender: isStandalonePage ? false : undefined,
    fixedHeader: !isStandalonePage,
    fixSiderbar: false,
    // 🔑 禁用 ProLayout 外层 PageContainer，避免其 header 在导航切换时短暂渲染导致面包屑+Tag栏跳动
    pageContainer: false,
    pageTitleRender: () => defaultSettings.title,
    collapsed,
    onCollapse: setCollapsed,
    onMenuHeaderClick: () => history.push('/'),
    // 自定义 Logo 和 Title 区域（高度由 CSS --header-bar-height 统一控制并随屏幕分档，与顶部栏对齐）
    logo: () => (
      <div className="sider-logo" style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        height: '100%',
        boxSizing: 'border-box',
        transition: 'justify-content 0.2s',
      }}>
        {/* Logo */}
        <img
          src="/logo.svg"
          alt="Logo"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
          }}
        />
        {/* Title - 换行显示；C 档（图标轨道）由 multi-level-menu.css 隐藏 */}
        {!collapsed && (
          <div className="sider-logo-text" style={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: 16,
            fontWeight: 500,
            // 跟随主题主色（亮 #1B6FE8 / 暗 #5B9DEF），见 styles/tokens.css 主题语义色层
            color: 'var(--primary)',
            lineHeight: 1.3,
            marginLeft: 8,
            justifyContent: 'center'
          }}>
            <span>LINSON MARINE</span>
            <span>管理系统</span>
          </div>
        )}
      </div>
    ),
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login（只排除对外展示页面 + PDA 小应用，登录页和注册页需要登录才能访问）
      if (
        !initialState?.currentUser &&
        !isAuthenticationExemptPath(location.pathname) &&
        !location.pathname.startsWith('/user/')
      ) {
        history.replace(loginPathForLocation(history.location));
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isStandalonePage ? [] : (isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : []),
    
    
    // 增加一个 loading 的状态
    childrenRender: (children, props) => {
      // 检查当前路由是否设置了 layout: false
      // 如果设置了 layout: false，直接返回 children，不包裹 TabLayout
      const currentPath = history.location.pathname;
      
      // 从 props 中获取路由信息（如果可用）
      const routePath = props?.location?.pathname || currentPath;
      
      const routeIsStandalonePage = isStandalonePagePath(routePath);
      
      if (routeIsStandalonePage) {
        // 独立页面，直接返回 children，不包裹任何布局
        return children;
      }
      
      // 注：全局初始化加载由 public/scripts/loading.js 统一处理（DOM 未挂载时的白屏保护），
      // 此处不再重复渲染全屏加载页，避免与 loading.js 出现双重加载动画。

      return (
        <ConfigProvider
          theme={ANT_D_CONFIG_THEME}
          locale={zhCN} // 添加 locale 配置
        >
          <RealtimeProvider>
            <TabLayout>
              {children}
            </TabLayout>
          </RealtimeProvider>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </ConfigProvider>
      );
    },
    // ⚠️ 从 settings 中排除 collapsed，防止 defaultSettings 的 collapsed:false 覆盖动态值
    ...(() => {
      if (!initialState?.settings) return {};
      const { collapsed: _c, ...rest } = initialState.settings as any;
      return rest;
    })(),
    /**
     * 🔑 navTheme 与 ProLayout token 是同一次渲染里成对下发的：
     *    navTheme 决定 ProLayout 给 antd Menu 的 `theme="dark"/"light"`（Menu 内部预设色
     *    不吃我们的 CSS 层），token 决定侧栏/顶栏的显式配色（显式 token 会**压过** navTheme
     *    预设，所以两者必须一起切，否则切暗色后侧栏仍是白底黑字）。
     *    它们写在 initialState.settings 之后（后写者胜）：settings 里那份 token 是静态亮色。
     */
    navTheme: darkMode ? 'realDark' : 'light',
    token: layoutToken,
    /**
     * 🔑 侧栏宽度 / 折叠宽度必须写在 settings 之后（后写者胜）：
     * initialState.settings 里带着 defaultSettings 的静态 siderWidth，
     * 放在它前面会被覆盖回固定 210px，档位就失效了。
     * 两者同源取自 --tier-sider-w / --tier-sider-collapsed-w（见上方 readTierPx）。
     */
    siderWidth: sidebarWidth,
    collapsedWidth: sidebarCollapsedWidth,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // baseURL: 'https://proapi.azurewebsites.net', // 注释掉外部API，使用本地mock数据
  ...errorConfig,
};

/** 路由事件持有切换帧，异步页面和请求各自持有任务，直到内容真正就绪。 */
export function onRouteChange({ isFirst }: { isFirst?: boolean }) {
  navPerf.onRouteChange(history.location.pathname + history.location.search);
  beginRoutePageLoading();
  // BrowserRoutes 在用户/菜单初始化放行后才挂载，首屏提示必须保持到这里。
  if (isFirst) finishBootPageLoading();
}

/**
 * @name rootContainer 全局容器
 * 包裹 ErrorBoundary 捕获页面渲染异常，
 * 防止一个页面报错导致整个布局崩溃、路由无法跳转
 */
import ErrorBoundary from '@/components/ErrorBoundary';
import { modalEnhancer } from '@/utils/modalEnhancer';
import { installChunkRecovery } from '@/utils/chunkRecovery';
import { installAppVersionWatch } from '@/utils/appVersion';

// 启动全局弹窗增强（拖拽 + 8方向缩放），所有 Modal / Modal.confirm 自动生效
if (typeof window !== 'undefined') {
  modalEnhancer.init();
  /**
   * 部署自愈（详见 docs/前端部署与刷新机制说明.md）：
   *   ① installChunkRecovery() —— 运行期分包 404 → 一次性重载（首屏之外原有的空白）；
   *   ② installAppVersionWatch() —— 低频比对 dist/version.json → 分级提示/自动刷新。
   * 两者都自带护栏（编辑态 / 未保存 / 弹窗 / 上传中 / 隐藏标签页 / PDA 一律不自动刷新），
   * 也都有防死循环台账，改动前请先读 src/utils/reloadGuard.ts。
   */
  installChunkRecovery();
  installAppVersionWatch();
}

export function rootContainer(container: React.ReactNode) {
  return React.createElement(ErrorBoundary, null,
    React.createElement(SessionHeartbeat), React.createElement(PdaRealtimeBridge), container);
}
